import { useToggle, useTranslation } from '@starsoft/common/hooks';
import { DatePickerProps } from '../props';
import { FieldValues } from 'react-hook-form';
import { Input } from '@starsoft/common/components';
import styles from '../Desktop/styles.module.scss';
import baseStyles from '../styles.module.scss';
import { AnimatePresence } from 'framer-motion';
import DatePickerMobileSelector from './Selector';
import React from 'react';

/**
 * Mobile version of the DatePicker component that displays a calendar input field
 * with a mobile-optimized date selector modal
 *
 * @component
 * @template T - Generic type extending FieldValues for form handling
 * @param {Object} props - Component props
 * @param {string} props.placeholder - Placeholder text to show when no date is selected
 * @param {Function} props.onChange - Callback function when date selection changes
 * @param {string|Date} props.value - Currently selected date value
 * @param {boolean} props.disabled - Whether the date picker is disabled
 * @param {boolean} props.readOnly - Whether the date picker is read-only
 * @param {boolean} props.isError - Whether there is a validation error
 * @param {string|string[]} props.errorMessage - Error message(s) to display
 * @returns {JSX.Element} Mobile date picker with input field and modal selector
 */
export default function DatePickerMobile<T extends FieldValues>({
  placeholder,
  onChange,
  value,
  disabled,
  readOnly,
  ...props
}: DatePickerProps<T>) {
  const [open, toggleOpen] = useToggle();
  const { lang } = useTranslation();

  return (
    <React.Fragment>
      <Input
        hasPaddingRight
        customIcon={
          <i
            className={`fa-solid fa-calendar ${baseStyles.icon}`}
            onClick={disabled || readOnly ? undefined : toggleOpen}
          />
        }
        onChange={e => onChange(e?.target?.value)}
        value={
          !!value
            ? Intl.DateTimeFormat(lang, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'UTC',
              }).format(new Date(String(value)))
            : undefined
        }
        disabled={disabled}
        readOnly
        hideLock
        placeholder={placeholder}
        {...props}
      />
      <AnimatePresence mode="wait">
        {open && (
          <DatePickerMobileSelector
            {...props}
            toggleOpen={toggleOpen}
            onChange={onChange}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
          />
        )}
      </AnimatePresence>{' '}
      {props?.isError &&
        !!props?.errorMessage &&
        (Array.isArray(props?.errorMessage) ? (
          props?.errorMessage.map((error, i) => (
            <span
              className={styles['container--error']}
              key={`error-message-${i}`}
            >
              *{error}
            </span>
          ))
        ) : (
          <span className={styles['container--error']}>
            *{props?.errorMessage}
          </span>
        ))}
    </React.Fragment>
  );
}
